/** Helpers for the bot's intent Quality view (hub /api/intents/bot/:botId/quality). Pure, tested in tests/. */

export interface QualityTotals {
  deflected: number;
  abstained: number;
  control: number;
  other: number;
  total: number;
}

/** Whole-number percentages of the total (0 when there is no traffic). */
export function shares(t: QualityTotals): { deflected: number; abstained: number; control: number; other: number } {
  const pct = (n: number) => (t.total > 0 ? Math.round((n / t.total) * 100) : 0);
  return { deflected: pct(t.deflected), abstained: pct(t.abstained), control: pct(t.control), other: pct(t.other) };
}

/** Same bounds the server enforces (bot_runtime_config intentMinScore / intentMinMargin). */
const BOUNDS = { score: [0.5, 0.99], margin: [0, 0.5] } as const;

/** A gate input box: blank = deployment default (null); otherwise a number within bounds. */
export function parseGateInput(raw: string, kind: 'score' | 'margin'): { value?: number | null; error?: string } {
  const s = raw.trim();
  if (!s) return { value: null };
  const n = Number(s);
  if (!Number.isFinite(n)) return { error: 'Enter a number, or leave blank for the default' };
  const [lo, hi] = BOUNDS[kind];
  if (n < lo || n > hi) return { error: `Must be between ${lo} and ${hi}` };
  return { value: n };
}

export type GateLoadState = 'loading' | 'ready' | 'none' | 'error';

/**
 * Whether the gate editor may save. Saving before the bot's config loaded (or after the load failed) would send blank
 * = "reset to default" and silently wipe a calibrated gate; a bot with no runtime config is refused by the server
 * (creating one would also set its flow version / active state for tool-service).
 */
export function gateEditor(state: GateLoadState): { enabled: boolean; note: string | null } {
  if (state === 'ready') return { enabled: true, note: null };
  if (state === 'loading') return { enabled: false, note: 'Loading…' };
  if (state === 'none') return { enabled: false, note: 'This bot has no runtime config yet — set it up in Config first.' };
  return { enabled: false, note: 'Could not load this bot’s config — reload to edit the gate.' };
}

const f2 = (n: number) => n.toFixed(2);

export function bars(buckets: { from: number; to: number; n: number }[]): { label: string; n: number; pct: number }[] {
  const max = Math.max(0, ...buckets.map((b) => b.n));
  return buckets.map((b) => ({ label: `${f2(b.from)}–${f2(b.to)}`, n: b.n, pct: max ? Math.round((b.n / max) * 100) : 0 }));
}

export function previewLine(p: { current: { answered: number }; candidate: { answered: number }; gained: number; lost: number }): string {
  return `answered ${p.current.answered} → ${p.candidate.answered} (+${p.gained} / −${p.lost})`;
}

export function parseGatePair(
  score: string,
  margin: string,
  bounds: { score: [number, number]; margin: [number, number] },
): { value?: { minScore: number; minMargin: number }; error?: string } {
  const s = Number(score.trim());
  const m = Number(margin.trim());
  if (!score.trim() || !margin.trim() || !Number.isFinite(s) || !Number.isFinite(m)) return { error: 'Enter both values as numbers' };
  if (s < bounds.score[0] || s > bounds.score[1]) return { error: `Score must be ${bounds.score[0]}–${bounds.score[1]}` };
  if (m < bounds.margin[0] || m > bounds.margin[1]) return { error: `Margin must be ${bounds.margin[0]}–${bounds.margin[1]}` };
  return { value: { minScore: s, minMargin: m } };
}

export function gateSourceLabel(v: {
  effective: { minScore: number; minMargin: number; source: 'bot' | 'legacy' | 'default' };
  own: { updatedBy: string | null; updatedAt: string } | null;
}): string {
  const pair = `(${f2(v.effective.minScore)} / ${f2(v.effective.minMargin)})`;
  if (v.effective.source === 'default') return `platform default ${pair}`;
  if (v.effective.source === 'legacy') return `legacy runtime config ${pair}`;
  return `set by ${v.own?.updatedBy ?? 'unknown'} on ${String(v.own?.updatedAt ?? '').slice(0, 10)} ${pair}`;
}

/**
 * Is this preview about exactly what is on screen? Out-of-order responses, a changed window, or a gate
 * saved/reset since the fetch must not be shown or confirmed (float4 values compared with a tolerance).
 */
export function previewMatches(
  p: { days: number; current: { minScore: number; minMargin: number }; candidate: { minScore: number; minMargin: number } } | null,
  candidate: { minScore: number; minMargin: number } | undefined,
  effective: { minScore: number; minMargin: number },
  days: number,
): boolean {
  if (!p || !candidate) return false;
  const eq = (a: number, b: number) => Math.abs(a - b) <= 1e-6;
  return (
    p.days === days &&
    eq(p.candidate.minScore, candidate.minScore) &&
    eq(p.candidate.minMargin, candidate.minMargin) &&
    eq(p.current.minScore, effective.minScore) &&
    eq(p.current.minMargin, effective.minMargin)
  );
}
